import { useMemo } from 'react';

import { tooltipClasses } from '@mui/material';
import CustomTooltip from 'components/CashbackBadge/CustomTooltip/CustomTooltip';
import LanguageSelect from 'components/LanguageSelect';
import i18n from 'i18n';
import { t } from 'i18next';
import type { Currency } from 'services/model';
import type { ICalculateWidgetResult, IWidgetItem } from 'services/WidgetService/widget.model';
import { WidgetTypesEnum } from 'services/WidgetService/widget.model';

import { getWidgetColors } from '../Widget.helper';

import { ReactComponent as IconCat } from 'assets/images/ui/icon-cat.svg';
import { ReactComponent as IconInfo } from 'assets/images/ui/icon-info.svg';
import { ReactComponent as IconPrimaryLogo } from 'assets/images/ui/icon-primary-logo.svg';
import { ReactComponent as IconQuestion } from 'assets/images/ui/icon-question-mark.svg';

import styles from '../Widget.module.scss';

interface IWidgetInfoHeaderProps {
  cashbackValue: string;
  selectedCurrency: Currency | null | undefined;
  amount?: string;
  calculateWidget?: ICalculateWidgetResult;
  isError?: boolean;
  widgetInfo?: IWidgetItem;
}

export const WidgetHeader: React.FC<IWidgetInfoHeaderProps> = ({
  widgetInfo,
  calculateWidget,
  selectedCurrency,
  amount,
  isError,
  cashbackValue,
}) => {
  const widgetColors = useMemo(
    () =>
      getWidgetColors({
        actionBackground: widgetInfo?.customization?.button_color ?? widgetInfo?.customization?.qr_color,
        contentBackground: widgetInfo?.customization?.background_color,
        headerBackground: widgetInfo?.customization?.header_color,
      }),
    [widgetInfo],
  );

  const isBackgroundGradient = useMemo(() => {
    if (!widgetInfo?.customization?.header_color) {
      return false;
    }

    if (widgetInfo?.customization?.header_color?.length > 1) {
      return true;
    }

    return false;
  }, [widgetInfo?.customization?.header_color]);

  const getHeaderAmount = () => {
    if (widgetInfo?.currency || selectedCurrency) {
      if (widgetInfo?.currency_type === 'fiat') {
        return (
          <>
            {(widgetInfo?.payer_amount ?? calculateWidget?.payer_amount) ? (
              <span>
                {widgetInfo?.payer_amount ?? calculateWidget?.payer_amount}{' '}
                {widgetInfo?.payment_currency ?? calculateWidget?.payment_currency}
              </span>
            ) : (
              <span
                className={styles.placeholder}
                style={{
                  color: widgetColors.placeholderHeaderTextColor,
                }}
              >
                {t('Select currency')}
              </span>
            )}
          </>
        );
      }

      if (widgetInfo?.amount || amount) {
        return (
          <>
            {widgetInfo?.amount ?? amount} {widgetInfo?.currency ?? selectedCurrency?.currency}
          </>
        );
      }

      return (
        <span
          className={styles.placeholder}
          style={{
            color: amount ? 'inherit' : widgetColors.placeholderHeaderTextColor,
          }}
        >
          {amount || t('Enter amount')} {widgetInfo?.currency ?? selectedCurrency?.currency}
        </span>
      );
    }

    return (
      <span
        className={styles.placeholder}
        style={{
          color: widgetColors.placeholderHeaderTextColor,
        }}
      >
        {t('Select currency')}
      </span>
    );
  };

  if (widgetInfo?.type !== WidgetTypesEnum.BUTTON) {
    return (
      <div
        className={styles.widgetHeader}
        style={{
          background: isBackgroundGradient
            ? `linear-gradient(280deg, ${widgetInfo?.customization.header_color?.[1]}, ${widgetInfo?.customization.header_color?.[0]}`
            : widgetInfo?.customization.header_color?.[0],
          color: widgetColors.textColor,
        }}
      >
        <div className={styles.headerTitle}>
          <div className={styles.logo}>
            <IconPrimaryLogo />
          </div>

          {!isError && (
            <div className={styles.cashbackWrapper}>
              <div
                className={styles.cashback}
                style={{
                  backgroundColor: widgetColors.backgroundColors,
                  color: widgetColors.cashbackColor,
                }}
              >
                <div
                  className={styles.cashbackLogo}
                  style={{
                    background: widgetColors.cashbackColor,
                    color: widgetColors.textColor,
                  }}
                >
                  <IconCat />
                </div>

                <span className={styles.cashbackValue}>+{cashbackValue} CPT</span>

                <CustomTooltip
                  arrow
                  data-debug
                  data-testid='my-tooltip'
                  direction={i18n.dir()}
                  placement='bottom-end'
                  triangleMobilePosition={0}
                  trianglePosition={0}
                  sx={{
                    [`& .${tooltipClasses.tooltip}`]: {
                      background: 'var(--black)',
                    },
                  }}
                  title={
                    <p className={styles.tooltip__descr}>
                      {t('If you’ll make the transaction on this invoice, you’ll get cashback. You need to')}{' '}
                      <a href='https://app.cryptomus.com/login?utm_source=cashback ' rel='noreferrer' target='_blank'>
                        {t('log in')}
                      </a>{' '}
                      {t('or')}{' '}
                      <a href='https://app.cryptomus.com/signup?utm_source=cashback ' rel='noreferrer' target='_blank'>
                        {t('create account')}
                      </a>{' '}
                      {t('to get it')}
                    </p>
                  }
                >
                  <IconQuestion className='tooltip_icon' />
                </CustomTooltip>
              </div>
            </div>
          )}

          <div className={styles.iconInt}>
            <LanguageSelect
              backgroundColor={widgetColors.backgroundColors}
              color={widgetColors.cashbackColor}
              size='small'
              type='widget'
            />
          </div>
        </div>

        {!isError && (
          <div
            className={styles.headerContent}
            style={{
              color: widgetColors.textColor,
            }}
          >
            {widgetInfo?.payment_name && (
              <p className={styles.paymentName} style={{ color: widgetColors.textColor }}>
                {widgetInfo?.payment_name}
              </p>
            )}
            <p className={styles.amount} style={{ color: widgetColors.textColor }}>
              {getHeaderAmount()}
            </p>

            <div className={styles.subAmount}>
              {widgetInfo?.currency_type === 'fiat' && (
                <span className={styles.fiat}>
                  {widgetInfo?.amount ?? ''} {widgetInfo?.currency ?? ''}
                </span>
              )}
              {widgetInfo?.currency_type === 'fiat' && !!widgetInfo?.network && ' / '}
              {widgetInfo?.network && <span className={styles.network}>{widgetInfo?.network ?? ''}</span>}
            </div>

            {!widgetInfo?.is_subtract && (
              <p
                className={styles.info}
                style={{
                  color: widgetColors.grayTextColor,
                }}
              >
                <span
                  className={styles.icon}
                  style={{
                    color: widgetColors.grayTextColor,
                  }}
                >
                  <IconInfo />
                </span>
                {t('You pay network fee')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};
